import { LitElement, css, html } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';
import { teknikTokens } from '../styles/teknik-tokens.js';
import './app-search.js';
import './notification-bell.js';

export type NavDest = {
  label: string;
  /** Path without the base prefix — resolveRouterPath() adds it. */
  path: string;
  /** Mask URL, kept for the drawer icons; the bar renders labels only. */
  icon: string;
  /** Dropped from both navs when signed out: the page needs an account. */
  authOnly?: boolean;
  match: (p: string) => boolean;
};

/* No book glyph exists in /assets/icons, and the live icons ship from dist/,
   which is built rather than edited — so this one rides along inline. */
const BOOK_ICON = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#000" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>' +
    '<path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>'
)}`;

/**
 * The things teknik.sh itself hosts, in nav order. Both navs render this one
 * list, rendered as labels in the bar and in the drawer, so they can no
 * longer disagree about what the site offers. Sibling products on their own
 * domains are outbound links, so they stay in the drawer's Elsewhere group.
 */
export const PRODUCTS: NavDest[] = [
  { label: 'Cloud', path: 'cloud', icon: '/assets/icons/database.svg', match: p => p.startsWith('/cloud') },
  { label: 'Messages', path: 'messages', icon: '/assets/icons/chat-dots.svg', authOnly: true, match: p => p.startsWith('/messages') },
  { label: 'Games', path: 'games', icon: '/assets/icons/controller.svg', match: p => p.startsWith('/games') },
  { label: 'Books', path: 'books', icon: BOOK_ICON, match: p => p.startsWith('/books') },
  { label: 'Store', path: 'store', icon: '/assets/icons/bag.svg', match: p => p.startsWith('/store') },
];

@customElement('app-header')
export class AppHeader extends LitElement {
  @property({ type: String }) logoUrl = '/assets/icons/256.png';
  @property({ type: String, attribute: 'pagetitle' }) pageTitle = '';
  @property({ type: Boolean }) enableBack = false;
  @property({ type: Boolean, reflect: true }) darkMode = false;
  @property({ type: String }) lastUpdated = '';
  @property({ type: Boolean }) isLoggedIn = false;
  @property({ type: Object }) userData: { firstName?: string; lastName?: string } | null = null;
  @property({ type: String }) userAvatar = '';
  @state() showAccountCenter = false;
  @state() minecraftStats = null;
  @state() loadingStats = false;
  @state() authStatus: any = null;
  @state() drawerOpen = false;

  static styles = [
    teknikTokens,
    css`
      :host {
        font-family: var(--font-family);
      }

      header {
        display: flex;
        align-items: center;
        gap: 1.5rem;
        color: var(--tk-text);
        padding: 0 16px;
        position: fixed;
        left: 0;
        top: 0;
        height: 48px;
        /* 100% not 100vw: 100vw includes the vertical scrollbar's width on
           desktop, making the fixed header wider than the viewport and forcing
           a horizontal scrollbar on any page tall enough to scroll. */
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        z-index: 1000;
        background: rgba(11, 14, 15, 0.82);
        -webkit-backdrop-filter: blur(18px) saturate(160%);
        backdrop-filter: blur(18px) saturate(160%);
        border-bottom: 1px solid var(--tk-border);
      }

      .brand {
        display: flex;
        align-items: center;
        gap: 9px;
        text-decoration: none;
        flex-shrink: 0;
      }

      /* teknik.svg is a 129x111 logomark, not a wordmark — size it to its own
         aspect, or mask-size contain silently leaves dead space beside it. */
      .mark {
        width: 22px;
        height: 19px;
        background: var(--tk-text);
        -webkit-mask-image: url(/assets/icons/teknik.svg);
        mask-image: url(/assets/icons/teknik.svg);
        -webkit-mask-size: contain;
        mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        mask-repeat: no-repeat;
        -webkit-mask-position: left center;
        mask-position: left center;
        transition: background 0.15s ease;
      }
      .brand:hover .mark { background: var(--tk-accent); }

      .wordmark {
        font-family: var(--font-display);
        font-size: 17px;
        font-weight: 600;
        letter-spacing: -0.005em;
        color: var(--tk-text);
      }

      .back-button {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--tk-panel-2);
        border: 1px solid var(--tk-border);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: border-color 0.15s ease;
        outline: none;
        flex-shrink: 0;
        padding: 0;
      }
      .back-button:hover { border-color: var(--tk-border-strong); }
      .back-button img { width: 14px; height: 14px; filter: invert(1) brightness(0.9); }

      /* ── Desktop nav ──────────────────────────────────────────────── */
      .nav {
        display: flex;
        align-items: center;
        gap: 1.35rem;
        min-width: 0;
      }

      .nav a {
        font-size: 13.5px;
        font-weight: 500;
        color: var(--tk-muted);
        text-decoration: none;
        white-space: nowrap;
        transition: color 0.15s ease;
      }
      .nav a:hover { color: var(--tk-text); }
      .nav a.active { color: var(--tk-accent); }

      /* Pushes the whole right-hand cluster over. Without this, hiding .nav at
         narrow widths leaves the bell and avatar huddled against the logo. */
      .actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex-shrink: 0;
      }

      .search {
        width: 240px;
        max-width: 30vw;
      }

      .icon-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border-radius: var(--tk-r-control);
        border: none;
        background: transparent;
        color: var(--tk-muted);
        cursor: pointer;
        text-decoration: none;
        transition: background 0.15s ease, color 0.15s ease;
        padding: 0;
      }
      .icon-btn:hover { background: var(--tk-panel-2); color: var(--tk-text); }

      .avatar-link {
        display: flex;
        align-items: center;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        border: 1px solid var(--tk-border-strong);
        transition: border-color 0.15s ease;
        text-decoration: none;
      }
      .avatar-link:hover { border-color: var(--tk-accent); }
      .avatar-link img { width: 100%; height: 100%; object-fit: cover; }

      .signin {
        font-size: 13px;
        font-weight: 500;
        color: var(--tk-text);
        text-decoration: none;
        border: 1px solid var(--tk-border-strong);
        border-radius: var(--tk-r-control);
        padding: 6px 12px;
        white-space: nowrap;
        transition: border-color 0.15s ease, color 0.15s ease;
      }
      .signin:hover { border-color: var(--tk-accent-line); color: var(--tk-accent); }

      .burger { display: none; }

      /* ── Drawer ───────────────────────────────────────────────────── */
      .scrim {
        position: fixed;
        inset: 48px 0 0 0;
        background: rgba(4, 6, 7, 0.6);
        z-index: 998;
        border: 0;
        padding: 0;
      }

      .drawer {
        position: fixed;
        top: 48px;
        left: 0;
        right: 0;
        z-index: 999;
        background: var(--tk-bg);
        border-bottom: 1px solid var(--tk-border);
        padding: 14px 16px 20px;
        display: flex;
        flex-direction: column;
        gap: 2px;
        max-height: calc(100vh - 48px);
        overflow-y: auto;
        box-sizing: border-box;
      }

      .drawer a {
        font-size: 15px;
        font-weight: 500;
        color: var(--tk-text);
        text-decoration: none;
        padding: 11px 8px;
        border-radius: var(--tk-r-control);
      }
      .drawer a:hover { background: var(--tk-panel); }
      .drawer a.active { color: var(--tk-accent); }

      .drawer-rule {
        margin: 10px 8px 6px;
        font-family: var(--font-mono);
        font-size: 10px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--tk-dim);
      }

      .drawer-search { padding: 4px 8px 10px; }

      /* Same breakpoint as .burger below: any wider gap leaves viewports with
         no bar search and no drawer to reach the other one. */
      @media (max-width: 768px) {
        .search { display: none; }
      }

      @media (max-width: 768px) {
        header { gap: 0.75rem; }
        .nav { display: none; }
        .burger {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        * { transition: none !important; }
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this.darkMode = true;
    fetch('/api/auth/status').then(r => r.json()).then(s => { this.authStatus = s; }).catch(() => {});
    window.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this._onKey);
    super.disconnectedCallback();
  }

  private _onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.drawerOpen) this.drawerOpen = false;
  };

  private _closeDrawer = () => { this.drawerOpen = false; };

  _getHeaderAvatar() {
    const user = this.authStatus?.user;
    if (!user) return 'https://cdn.discordapp.com/embed/avatars/0.png';
    // Teknik avatar (data URL or null)
    if (user.avatar?.startsWith('data:')) return user.avatar;
    // Discord avatar
    const discordId = user.discordId || user.id;
    if (user.discordAvatar && discordId) return `https://cdn.discordapp.com/avatars/${discordId}/${user.discordAvatar}.png?size=64`;
    if (user.avatar && discordId) return `https://cdn.discordapp.com/avatars/${discordId}/${user.avatar}.png?size=64`;
    return `https://cdn.discordapp.com/embed/avatars/0.png`;
  }

  goBack() {
    window.history.back();
  }

  navigateToAccount() {
    if (this.isLoggedIn) {
      window.location.href = '/account';
    } else {
      window.location.href = '/auth/discord';
    }
  }

  private _isActive(href: string) {
    const path = window.location.pathname;
    return href === '/' ? path === '/' : path.startsWith(href);
  }

  render() {
    const signedIn = !!this.authStatus?.authenticated;
    const path = window.location.pathname;
    const dests = PRODUCTS.filter(d => signedIn || !d.authOnly);

    return html`
      <header>
        <a class="brand" href="${resolveRouterPath()}" aria-label="Teknik home">
          <span class="mark" aria-hidden="true"></span>
          <span class="wordmark">Teknik</span>
        </a>
        ${this.enableBack
          ? html`<button class="back-button" @click="${this.goBack}" aria-label="Go back">
              <img src="assets/icons/back.svg" alt="" />
            </button>`
          : ''}

        <!-- The brand mark is the home link, so the bar carries products only. -->
        <nav class="nav">
          ${dests.map(
            d => html`<a class="${d.match(path) ? 'active' : ''}" href="${resolveRouterPath(d.path)}">${d.label}</a>`
          )}
        </nav>

        <div class="actions">
          <div class="search"><app-search></app-search></div>

          ${signedIn
            ? html`
                <notification-bell></notification-bell>
                <a class="avatar-link" href="${resolveRouterPath('account')}" title="Account" aria-label="Account">
                  <img src="${this._getHeaderAvatar()}" alt=""
                       onerror="this.src='https://cdn.discordapp.com/embed/avatars/0.png'" />
                </a>
              `
            : html`<a class="signin" href="${resolveRouterPath('login')}">Sign in</a>`}

          <button
            class="icon-btn burger"
            @click=${() => (this.drawerOpen = !this.drawerOpen)}
            aria-label="${this.drawerOpen ? 'Close menu' : 'Open menu'}"
            aria-expanded="${this.drawerOpen}"
          >
            ${this.drawerOpen
              ? html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
                       <path d="M18 6 6 18M6 6l12 12"/></svg>`
              : html`<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          stroke-width="1.8" stroke-linecap="round" aria-hidden="true">
                       <path d="M4 7h16M4 12h16M4 17h16"/></svg>`}
          </button>
        </div>
      </header>

      ${this.drawerOpen
        ? html`
            <button class="scrim" aria-label="Close menu" @click=${this._closeDrawer}></button>
            <div class="drawer">
              <div class="drawer-search"><app-search></app-search></div>
              <a class="${this._isActive('/') ? 'active' : ''}" href="${resolveRouterPath()}"
                 @click=${this._closeDrawer}>Home</a>
              ${dests.map(
                d => html`<a class="${d.match(path) ? 'active' : ''}" href="${resolveRouterPath(d.path)}"
                             @click=${this._closeDrawer}>${d.label}</a>`
              )}
              <div class="drawer-rule">Account</div>
              ${signedIn
                ? html`
                    <a class="${this._isActive('/account') ? 'active' : ''}" href="${resolveRouterPath('account')}"
                       @click=${this._closeDrawer}>Account</a>
                    <a class="${this._isActive('/billing') ? 'active' : ''}" href="${resolveRouterPath('billing')}"
                       @click=${this._closeDrawer}>Billing</a>
                  `
                : html`<a href="${resolveRouterPath('login')}" @click=${this._closeDrawer}>Sign in</a>`}
              <div class="drawer-rule">Elsewhere</div>
              <a href="https://lunako.app" target="_blank" rel="noopener" @click=${this._closeDrawer}>Luna</a>
              <a href="https://sumiko.app" target="_blank" rel="noopener" @click=${this._closeDrawer}>Sumiko</a>
              <a href="https://kohosting.app" target="_blank" rel="noopener" @click=${this._closeDrawer}>KO Hosting</a>
            </div>
          `
        : ''}
    `;
  }
}
